import React, { useState } from 'react';
import { X, Link as LinkIcon, Mail, Copy, Check, Shield, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: {
    type: 'personal' | 'business';
    score: number;
    id?: string;
  };
  onSuccess: (message: string) => void;
}
export function ShareReportModal({
  isOpen,
  onClose,
  reportData,
  onSuccess
}: ShareReportModalProps) {
  const [activeTab, setActiveTab] = useState<'link' | 'email'>('link');
  const [shareScope, setShareScope] = useState<'full' | 'summary' | 'summary_actions'>('summary_actions');
  const [viewOnly, setViewOnly] = useState(true);
  const [allowDownload, setAllowDownload] = useState(false);
  const [expiry, setExpiry] = useState('7');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [requireVerification, setRequireVerification] = useState(true);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;
  const generatedLink = `https://app.lawvo.com/shared-report/${reportData.id || 'req_8f92a1'}`;
  const themeColor = reportData.type === 'personal' ? 'text-violetBlue' : 'text-turquoise';
  const themeBg = reportData.type === 'personal' ? 'bg-violetBlue' : 'bg-turquoise';
  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCreateLink = () => {
    setLinkGenerated(true);
    onSuccess('Secure link created');
  };
  const handleSendEmail = () => {
    if (!email) return;
    setEmailSent(true);
    onSuccess('Report sent successfully');
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  const handleClose = () => {
    setLinkGenerated(false);
    setEmailSent(false);
    setCopied(false);
    setEmail('');
    setMessage('');
    onClose();
  };
  const scopeOptions = [{
    value: 'full' as const,
    label: 'Full Report',
    desc: 'Includes all scores, answers, and detailed action plan.'
  }, {
    value: 'summary_actions' as const,
    label: 'Summary + Recommended Actions',
    desc: 'Hides detailed answers but shows next steps.'
  }, {
    value: 'summary' as const,
    label: 'Summary Only',
    desc: 'Only shows the overall score and top issues.'
  }];
  return <>
      <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={handleClose} data-id="element-164" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col max-h-[90vh]" data-id="element-165">
        {/* Header */}
        <div className="px-6 py-4 border-b border-borderColor flex items-center justify-between bg-white sticky top-0 z-10" data-id="element-166">
          <div data-id="element-167">
            <h2 className="text-xl font-bold text-primary" data-id="element-168">Share Report</h2>
            <p className="text-xs text-secondary mt-1" data-id="element-169">
              Create a secure LawVo-branded share link or send directly.
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-aliceBlue rounded-lg transition-colors" data-id="element-170">
            <X className="w-5 h-5 text-secondary" data-id="element-171" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto" data-id="element-172">
          {!linkGenerated && !emailSent ? <>
              {/* Tabs */}
              <div className="flex p-1 bg-aliceBlue rounded-lg mb-6" data-id="element-173">
                <button onClick={() => setActiveTab('link')} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'link' ? 'bg-white text-primary shadow-sm' : 'text-secondary hover:text-primary'}`} data-id="element-174">
                  <LinkIcon className="w-4 h-4" data-id="element-175" />
                  Copy Secure Link
                </button>
                <button onClick={() => setActiveTab('email')} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'email' ? 'bg-white text-primary shadow-sm' : 'text-secondary hover:text-primary'}`} data-id="element-176">
                  <Mail className="w-4 h-4" data-id="element-177" />
                  Email Report
                </button>
              </div>

              {/* Scope Selector */}
              <div className="mb-6" data-id="element-178">
                <label className="block text-sm font-semibold text-primary mb-3" data-id="element-179">
                  What to share
                </label>
                <div className="space-y-2" data-id="element-180">
                  {scopeOptions.map(opt => <label key={opt.value} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${shareScope === opt.value ? 'border-borderColor bg-aliceBlue' : 'border-borderColor hover:bg-gray-50'}`} data-id="element-181">
                      <input type="radio" name="scope" value={opt.value} checked={shareScope === opt.value} onChange={() => setShareScope(opt.value)} className={`mt-0.5 w-4 h-4 ${themeColor}`} data-id="element-182" />
                      <div className="ml-3" data-id="element-183">
                        <p className="text-sm font-medium text-primary" data-id="element-184">
                          {opt.label}
                        </p>
                        <p className="text-xs text-secondary" data-id="element-185">{opt.desc}</p>
                      </div>
                    </label>)}
                </div>
              </div>

              {/* Access Controls */}
              <div className="mb-6 bg-gray-50 p-4 rounded-xl border border-borderColor" data-id="element-186">
                <div className="flex items-center gap-2 mb-3" data-id="element-187">
                  <Shield className="w-4 h-4 text-secondary" data-id="element-188" />
                  <h3 className="text-sm font-semibold text-primary" data-id="element-189">
                    Access Controls
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-190">
                  <div data-id="element-191">
                    <label className="block text-xs font-medium text-secondary mb-1" data-id="element-192">
                      Link expires after
                    </label>
                    <select value={expiry} onChange={e => setExpiry(e.target.value)} className="w-full border border-borderColor rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue bg-white" data-id="element-193">
                      <option value="7" data-id="element-194">7 days</option>
                      <option value="30" data-id="element-195">30 days</option>
                      <option value="none" data-id="element-196">No expiry</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-197">
                    <span className="text-sm text-primary" data-id="element-198">View-only</span>
                    <button onClick={() => setViewOnly(!viewOnly)} className={`relative w-11 h-6 rounded-full transition-colors ${viewOnly ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-199">
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${viewOnly ? 'translate-x-5' : ''}`} data-id="element-200" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-201">
                    <span className="text-sm text-primary" data-id="element-202">
                      Allow PDF download
                    </span>
                    <button onClick={() => setAllowDownload(!allowDownload)} className={`relative w-11 h-6 rounded-full transition-colors ${allowDownload ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-203">
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${allowDownload ? 'translate-x-5' : ''}`} data-id="element-204" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Email-specific fields */}
              {activeTab === 'email' && <div className="mb-6 space-y-4" data-id="element-205">
                  <div data-id="element-206">
                    <label className="block text-sm font-medium text-primary mb-2" data-id="element-207">
                      Recipient email
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="w-full border border-borderColor rounded-lg px-4 py-2.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue" data-id="element-208" />
                  </div>
                  <div data-id="element-209">
                    <label className="block text-sm font-medium text-primary mb-2" data-id="element-210">
                      Message (optional)
                    </label>
                    <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a personal note..." rows={2} className="w-full border border-borderColor rounded-lg px-4 py-2.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue resize-none" data-id="element-211" />
                  </div>
                  <div className="flex items-center justify-between" data-id="element-212">
                    <span className="text-sm text-primary" data-id="element-213">
                      Require email verification to view
                    </span>
                    <button onClick={() => setRequireVerification(!requireVerification)} className={`relative w-11 h-6 rounded-full transition-colors ${requireVerification ? 'bg-turquoise' : 'bg-disabled'}`} data-id="element-214">
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${requireVerification ? 'translate-x-5' : ''}`} data-id="element-215" />
                    </button>
                  </div>
                </div>}

              {/* Security note */}
              <div className="flex items-start gap-2 mb-6 p-3 bg-aliceBlue rounded-lg border border-borderColor" data-id="element-216">
                <AlertCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" data-id="element-217" />
                <p className="text-xs text-secondary leading-relaxed" data-id="element-218">
                  This report contains personal legal information. Shared links
                  are encrypted and access-controlled. Recipients will see a
                  LawVo-branded viewer.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex gap-3" data-id="element-219">
                <button onClick={handleClose} className="flex-1 px-4 py-3 border border-borderColor text-primary rounded-lg hover:bg-aliceBlue transition-colors text-sm font-medium" data-id="element-220">
                  Cancel
                </button>
                <button onClick={activeTab === 'link' ? handleCreateLink : handleSendEmail} disabled={activeTab === 'email' && !email} className={`flex-1 px-4 py-3 ${themeBg} text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-221">
                  {activeTab === 'link' ? 'Create Share Link' : 'Send Report'}
                </button>
              </div>
            </> : linkGenerated ? <div className="flex flex-col items-center py-6" data-id="element-222">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6" data-id="element-223">
                <Check className="w-8 h-8 text-green-500" data-id="element-224" />
              </motion.div>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-225">
                Secure Link Created
              </h3>
              <p className="text-sm text-secondary mb-6 text-center" data-id="element-226">
                Share this link with your recipient. It will expire in{' '}
                {expiry === 'none' ? 'never' : `${expiry} days`}.
              </p>

              <div className="w-full flex items-center gap-2 bg-aliceBlue border border-borderColor rounded-lg p-3 mb-6" data-id="element-227">
                <input type="text" readOnly value={generatedLink} className="flex-1 bg-transparent text-sm text-primary truncate focus:outline-none" data-id="element-228" />
                <button onClick={handleCopyLink} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${copied ? 'bg-green-100 text-green-700' : `${themeBg} text-white hover:opacity-90`}`} data-id="element-229">
                  {copied ? <Check className="w-4 h-4" data-id="element-230" /> : <Copy className="w-4 h-4" data-id="element-231" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <button onClick={handleClose} className="text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-232">
                Close
              </button>
            </div> : <div className="flex flex-col items-center py-6" data-id="element-233">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6" data-id="element-234">
                <Mail className="w-8 h-8 text-green-500" data-id="element-235" />
              </motion.div>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-236">
                Report Sent
              </h3>
              <p className="text-sm text-secondary text-center" data-id="element-237">
                Your report has been sent to <strong data-id="element-238">{email}</strong>.
              </p>
            </div>}
        </div>
      </div>
    </>;
}